/**
 * "Move to folder" for one message in a mail server folder.
 *
 * The folder list is read when the dialog opens rather than handed over by the
 * rail: the rail and the message list live in different panes, and a list read
 * a minute ago may already name a folder somebody else has since renamed. The
 * server checks the destination again either way; this dialog only makes sure
 * the choices on offer are the ones the server has now.
 *
 * The folder the message is already in is not offered, nor is a \Noselect
 * node - neither can hold it, and a choice that can only fail is not a choice.
 */
import { useEffect, useState } from "react";
import { FolderInput, Loader2 } from "lucide-react";
import { useT } from "../../../../i18n/I18nProvider";
import { getImapFolders, type ImapFolder, type ImapMessageRow } from "../../../../lib/mail";
import { Btn, Modal, SelectInput } from "../../../ui";
import { imapReasonText } from "./imapFolderErrors";

export function MoveImapMessageModal({
  mailbox,
  path,
  row,
  onClose,
  onMove,
}: {
  /** The mailbox the folder is in: "shared" or an own mailbox id. */
  mailbox: string;

  /** The folder the message is in. */
  path: string;
  row: ImapMessageRow;
  onClose: () => void;
  /** Awaited, so the button stays busy until the message has actually moved. */
  onMove: (to: string) => Promise<void>;
}) {
  const t = useT();
  const [folders, setFolders] = useState<ImapFolder[] | null>(null);
  const [reason, setReason] = useState<string | null>(null);
  const [to, setTo] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let alive = true;
    getImapFolders(mailbox)
      .then((res) => {
        if (!alive) return;
        setFolders(res.ok ? res.folders.filter((f) => f.selectable && f.path !== path) : []);
        setReason(res.ok ? null : (res.reason ?? "imap_error"));
      })
      .catch(() => {
        if (!alive) return;
        setFolders([]);
        setReason("imap_error");
      });
    return () => {
      alive = false;
    };
  }, [path, mailbox]);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    if (!to || busy) return;
    setBusy(true);
    try {
      await onMove(to);
      onClose();
    } catch {
      // The toast has already said why; the dialog stays so another folder
      // can be picked.
    } finally {
      setBusy(false);
    }
  }

  return (
    <Modal title={t("email.imap.msg.moveTitle")} label={t("email.imap.msg.moveTitle")} onClose={onClose} className="max-w-[440px]">
      <form onSubmit={submit} className="mt-1 flex flex-col gap-3">
        <p className="crm-sub">{row.subject || t("email.imap.browse.noSubject")}</p>

        {folders === null ? (
          <span className="crm-mut flex items-center gap-2 text-[12.5px]">
            <Loader2 className="size-3.5 animate-spin" />
            {t("email.imap.loading")}
          </span>
        ) : reason ? (
          <p className="text-[12.5px] leading-snug text-destructive">{imapReasonText(t, reason, "email.imap.listFailed")}</p>
        ) : (
          <div>
            <span className="crm-lab">{t("email.imap.msg.moveTo")}</span>
            <SelectInput value={to} onChange={setTo} label={t("email.imap.msg.moveTo")}>
              <option value="">{t("email.imap.msg.movePick")}</option>
              {/* A nested folder is named by its whole path. Indenting with
                  spaces does not survive the dropdown, and "2026" alone does
                  not say whose 2026 it is. */}
              {folders.map((f) => (
                <option key={f.path} value={f.path}>
                  {f.depth ? f.path : f.name}
                </option>
              ))}
            </SelectInput>
          </div>
        )}

        <div className="crm-m-foot">
          <Btn type="button" onClick={onClose} disabled={busy}>
            {t("common.cancel")}
          </Btn>
          <Btn type="submit" tone="dark" disabled={busy || !to}>
            {busy ? <Loader2 className="size-3.5 animate-spin" /> : <FolderInput className="size-3.5" />}
            {t("email.imap.msg.moveGo")}
          </Btn>
        </div>
      </form>
    </Modal>
  );
}
